/**
 * Contact Validation Utilities for FSP Frontend
 * 
 * Email and phone validation/normalization.
 * Mirrors backend logic to provide pre-submit validation.
 */

// ==============================================================================
// EMAIL VALIDATION
// ==============================================================================

/**
 * Normalize email: trim whitespace, convert to lowercase
 * @param {string} email - Raw email input
 * @returns {string} Normalized email or empty string
 */
export function normalizeEmail(email) {
  if (!email) return "";
  return email.trim().toLowerCase();
}

/**
 * Basic email regex validation
 * - Local part: letters, digits, plus common symbols
 * - Domain: letters, digits, hyphens, dots
 * - TLD: at least 2 characters
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Check if email has valid structure
 * @param {string} email - Email to validate (should be normalized)
 * @returns {boolean} True if valid or empty
 */
export function isValidEmail(email) {
  if (!email) return true; // Empty is allowed
  return EMAIL_REGEX.test(email);
}

/**
 * Get human-readable error for invalid email
 * @param {string} email - Email to check
 * @returns {string|null} Error message or null if valid
 */
export function getEmailError(email) {
  if (!email) return null;
  
  if (email.includes(' ')) {
    return "Email cannot contain spaces";
  }
  
  if (!email.includes('@')) {
    return "Email must contain @";
  }
  
  const parts = email.split('@');
  if (parts.length !== 2) {
    return "Email must have exactly one @";
  }
  
  const [local, domain] = parts;
  
  if (!local) {
    return "Email must have text before @";
  }
  
  if (!domain) {
    return "Email must have a domain after @";
  }
  
  if (!domain.includes('.')) {
    return "Email domain must include a TLD (e.g., .com)";
  }
  
  if (domain.includes('..')) {
    return "Email domain cannot have consecutive dots";
  }
  
  if (!EMAIL_REGEX.test(email)) {
    return "Please enter a valid email like name@example.com";
  }
  
  return null;
}

// ==============================================================================
// PHONE VALIDATION (US ONLY)
// ==============================================================================

/**
 * Extract only digits from phone string
 * @param {string} phone - Phone string
 * @returns {string} Digits only
 */
function extractDigits(phone) {
  if (!phone) return "";
  return phone.replace(/\D/g, '');
}

/**
 * Normalize phone to US format: (###) ###-####
 * @param {string} phone - Raw phone input
 * @returns {string|null} Formatted phone or null if invalid
 */
export function normalizeUsPhone(phone) {
  if (!phone) return null;
  
  const digits = extractDigits(phone);
  
  if (!digits) return null;
  
  // 10 digits: standard US number
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  
  // 11 digits starting with 1: country code prefix
  if (digits.length === 11 && digits[0] === '1') {
    const withoutCountry = digits.slice(1);
    return `(${withoutCountry.slice(0, 3)}) ${withoutCountry.slice(3, 6)}-${withoutCountry.slice(6, 10)}`;
  }
  
  // Invalid
  return null;
}

/**
 * Check if phone is valid (can be normalized to US format)
 * @param {string} phone - Phone to validate
 * @returns {boolean} True if valid or empty
 */
export function isValidUsPhone(phone) {
  if (!phone) return true; // Empty is allowed
  return normalizeUsPhone(phone) !== null;
}

/**
 * Get human-readable error for invalid phone
 * @param {string} phone - Phone to check
 * @returns {string|null} Error message or null if valid
 */
export function getPhoneError(phone) {
  if (!phone) return null;
  
  const digits = extractDigits(phone);
  
  if (!digits) {
    return "Phone number must contain digits";
  }
  
  if (digits.length < 10) {
    return "Please enter a 10-digit US phone number";
  }
  
  if (digits.length === 10) {
    return null; // Valid
  }
  
  if (digits.length === 11) {
    if (digits[0] === '1') {
      return null; // Valid with country code
    }
    return "11-digit phone must start with country code 1";
  }
  
  // Too many digits
  return "Please enter a 10-digit US phone number";
}

// ==============================================================================
// CONVENIENCE: Validate both
// ==============================================================================

/**
 * Validate and get errors for customer contact info
 * @param {string} email - Email to validate
 * @param {string} phone - Phone to validate
 * @returns {{ emailError: string|null, phoneError: string|null, isValid: boolean }}
 */
export function validateCustomerContact(email, phone) {
  const normalizedEmail = normalizeEmail(email);
  const emailError = getEmailError(normalizedEmail);
  const phoneError = getPhoneError(phone);
  
  return {
    emailError,
    phoneError,
    isValid: !emailError && !phoneError,
    normalizedEmail: normalizedEmail || null,
    normalizedPhone: normalizeUsPhone(phone)
  };
}
